import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FieldPath, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ClearIcon from '@mui/icons-material/Clear';

import MoreIcon from '@mui/icons-material/More';

import BoltIcon from '@mui/icons-material/Bolt';
import PrintIcon from '@mui/icons-material/Print';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import useUtils from 'library/utils';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import {
  currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom,
  isSaveLoadingAtom, isWaitingBoxShowAtom, currentUserSessionAtom, allProductsAtom, allProductsWithOptionsAtom
} from 'library/store';
import useBillingService, { useBasicFilterBilling } from './services/Billing';
import useTellerOperationService from './services/TellerOperation';

import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Chip, Divider, IconButton, InputAdornment, Link, ListItemIcon, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import NumberFormat, { NumberFormatValues } from 'react-number-format';

import { IBilling, defaultBilling, IBillingDetail, defaultBillingDetail, ISale, defaultSale } from './models/Billing';

import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';

import { globalConfig } from 'config';

import useEnumerationService, {
  useBasicFilterEnumeration, useEnumerationItemCreateUpdate,
} from 'features/configuration/services/Enumeration';

import { format } from "date-fns";


import IEnumeration, {
  IEnumerationItem, Enum_SERVICE_TASK, Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE,
  Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_PRICE_PURPOSE,
  Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_CORPORATION_TYPE,
  Enum_RENTAL_PERIODICITY,
  Enum_CURRENCY, Enum_PAYMENT_SOURCE,
  Enum_CONSUMPTION_OPTION,
  Enum_BILLING_TERM_REFERENCE,
  defaultEnumerationItem,
  Enum_DEFAULT_BILLING_REFERENCE,
  Enum_LINE_OF_BUSINESS
} from 'features/configuration/models/Enumeration';

import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import useProductService, { useBasicFilterProduct, useBasicFilterProductWithOptions } from 'features/setup/services/Product';

import useApplicationSetupService from 'features/setup/services/ApplicationSetup';

import { defaultProductOption, IProduct, IProductOption, ProductType } from 'features/setup/models/Product';
import { defaultStore } from 'features/configuration';
import { isFalsy } from 'utility-types';
import { IStore } from 'features/configuration/models/Store';
import { typographyGroupBoxStyling } from 'themes/commonStyles';
import { debounce, sum } from 'lodash';
import BasicButtonList from 'components/ui/BasicButtonList';
import TextFieldCenter from 'components/ui/TextFieldCenter';
import { BasicBillingDetailForm } from './BasicBillingDetailForm';
import { MessageBoxDialog } from 'components/ui/MessageBoxDialog';
import { DatePicker } from '@mui/x-date-pickers';
import { BasicPersonWithMoreForm } from 'features/production/BasicPersonWithMoreForm';
import { defaultPerson } from 'features/production';
import { BasicPersonForm } from 'features/production/BasicPersonForm';
import { BasicBillingInfoForm } from './BasicBillingInfoForm';
import { IGroupBillingBilling } from './models/GroupBilling';
import EnhancedTable from 'components/ui/EnhancedTable';
import { ITellerOperation } from './models/TellerOperation';

import { motion } from 'framer-motion';

const SCROLL_OFFSET = 220;
const EPSILON = 2;

export const SaleForm: FC<IBilling> = (props: IBilling = defaultBilling) => {


  const MotionChip = motion(Chip);


  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isWaitingBoxShow, setIsWaitingBoxShow] = useRecoilState(isWaitingBoxShowAtom);

  const { id } = useParams();

  const { language: lg, taskCodes, userDescription, userName, userAlias,
    employeeId, roleEntities, applicationSetup, userStores } = useRecoilValue(currentUserSessionAtom);

  const allProducts = useRecoilValue(allProductsAtom);
  const [allProductsWithOptions, setAllProductsWithOptions] = useRecoilState(allProductsWithOptionsAtom);

  const { range, addDurationInPeriod } = useUtils();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer, openEntityPrintDrawer, checkEntitySaveAuthorization } = entityService();

  const { createBilling, updateBilling, evaluateAddedProduct,
    getCommissionDistributionsByBilling, deliverBillingDetail, getGroupBillingBillings } = useBillingService();

  const { getTellerOperationsByBilling } = useTellerOperationService();

  const { getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();
  const { getDefaultProductType } = useApplicationSetupService();

  const { getBasicProducts, getBasicProductWithOptions, getProductsWithOptions, getProductTypeNumber } = useProductService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);


  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterBilling = useBasicFilterBilling(
    (event: React.MouseEvent<unknown>, row: IBilling) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const emptyFunc = (obj: any) => { }


  const [currentProductOptions, setCurrentProductOptions] = useState<IProductOption[]>([]);
  const [productFilters, setProductFilters] = useState<IProduct[]>([]);

  const [products, setProducts] = useState<IProduct[]>([]);

  const refCurrencyCode = useRef<string>('');
  const refShowroomId = useRef<number>(-1);

  const refProductFilter = useRef<number>(0);

  const dt = new Date();
  const methods = useForm<IBilling>({
    defaultValues: {
      ...defaultBilling,
      currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate,
      showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      reference: `${userAlias ?? ''}-${format(dt, 'yyyy/MM/dd-HH')}h${format(dt, 'mm')}`

    }
  });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  let { fields: billingDetails, append: appendBillingDetails, update: updateBillingDetail, remove: removeBillingDetail, } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
    name: `billingDetails`,
    control,
  });


  const [openProductFilter, setOpenProductFilter] = useState(false); // 1
  const basicFilterProduct = useBasicFilterProductWithOptions(0, getValues().showroomId, getValues().currencyCode,
    async (event: React.MouseEvent<unknown>, row: IProduct) => {
      const { id, description, name, serviceDurationInMinute, isActive, lineOfBusinessCode, productOptions } = row;

      if (!isActive) {
        enqueueSnackbar(`${t('This product is not active')} : ${name}`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });
        return;
      }

      // Add product by handle click...
      handleProductClick(id);
      setOpenProductFilter(false);
    }
  );

  const watchIsDirectSale = watch('isDirectSale');

  const watchDefaultTaxRate = watch('defaultTaxRate');
  const watchShowroomId = watch('showroomId');
  const watchCurrencyCode = watch('currencyCode');

  const watchCustomerId = watch('customerId');

  const watchDiscount = watch('discount');

  const watchIsProforma = watch('isProforma');

  const methodsFilter = useForm<{ name: string, code: string }>({ defaultValues: { code: '', name: '' } });
  const { register: registerFilter, setValue: setValueFilter, getValues: getValuesFilter, reset: resetFilter, watch: watchFilter, control: controlFilter } = methodsFilter;

  const watchProductFilterName = watchFilter('code');
  const watchProductFilterCode = watchFilter('name');

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<IBilling>, Error, IBilling>(
    _id > 0 ? updateBilling : createBilling, {
    onSuccess: (data: IResult<IBilling>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);
      setCurrentFormNameAtom(watchIsDirectSale ? `${t('Direct sale')} - # ${data.data.id} # -` :
        `${t('Billing')} - ${t('Sale')} - # ${data.data.id} # -`);

      //queryClient.invalidateQueries(['Sale',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });

  const { data: _data, refetch } = useQuery<IBilling>(['Billing', _id], () => retrieveEntity('Billing', _id, 'Full'),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Billing'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE, Enum_BILLING_TERM_REFERENCE, Enum_PAYMENT_SOURCE,
      Enum_PRICE_PURPOSE, Enum_CURRENCY, Enum_CORPORATION_TYPE, Enum_DEFAULT_BILLING_REFERENCE, Enum_LINE_OF_BUSINESS]));

  const { data: enumItems2 } = useQuery<IEnumerationItem[]>(['EnumerationItems2', 'Billing'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_SERVICE_TASK,
      Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_CONSUMPTION_OPTION,
      Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_RENTAL_PERIODICITY]));

  const { data: store } = useQuery<IStore>(['Billing', 'Store', getValues().showroomId], () =>
    retrieveEntity('Store', getValues().showroomId), { refetchOnWindowFocus: false, enabled: getValues().showroomId > 0 });

  const { data: groupBillingBillings } = useQuery<IGroupBillingBilling[]>(['Billing', 'GroupBillingBilling', getValues().id], () =>
    getGroupBillingBillings(getValues().id), { refetchOnWindowFocus: true, enabled: getValues().id > 0 });

  // const {data: tellerOperations} = useQuery<ITellerOperation[]>( ['Billing', 'TellerOperation', getValues().id], () => 
  //   getTellerOperationsByBilling(getValues().id), {refetchOnWindowFocus: true, enabled: getValues().id > 0 } );


  const [tellerOperations, setTellerOperations] = useState<ITellerOperation[]>([]);

  const [openCustomerDetails, setOpenCustomerDetails] = useState<boolean>(false);
  const handleClickOpenCustomer = async (event: any) => {

    setOpenCustomerDetails(true);
  }

  const [showDefaultReference, setShowDefaultReference] = useState<boolean>(false);
  const handleClickOpenDefaultReference = async (event: any) => {
    setShowDefaultReference(true);
  }

  const handleDefaultReferenceChoiceClick = async (defaultReference: IEnumerationItem) => {

    setValue('reference', defaultReference.name, { shouldValidate: true });
    setShowDefaultReference(false);
  }



  const handleClickRemoveCustomer = async (event: any) => {
    (setValue as any)('customerId', 0 );
    setValue('customer', defaultPerson);
  }

  const [openBillingInfo, setOpenBillingInfo] = useState<boolean>(false);
  const handleLinkClickBillingInfo = (event: any) => {
    setOpenBillingInfo(true);
  }


  const [lineOfBusinessCode, setLineOfBusinessCode] = useState<string>(applicationSetup.firstLineOfBusinessCode);
  const refLineOfBusinessCode = useRef<string>(applicationSetup.firstLineOfBusinessCode);

  const handleLineOfBusiness = async (newLineOfBusinessCode: string) => {
    if (newLineOfBusinessCode === null) return;
    setLineOfBusinessCode(newLineOfBusinessCode);
    refLineOfBusinessCode.current = newLineOfBusinessCode; //getProductTypeNumber(newProductType);

    const code = getValuesFilter().code;
    const name = getValuesFilter().name;

    const arr = (allProducts.length > 0 && applicationSetup.loadProductOnLogon) ?
      allProducts.filter(p => p.code.toLowerCase().includes(code.toLowerCase()) &&
        p.name.toLowerCase().includes(name.toLowerCase()) &&
        p.lineOfBusinessCode.includes(refLineOfBusinessCode.current))
      : await getBasicProducts(0, getValues().showroomId, getValues().currencyCode, {
        isActive: true,
        code: getValuesFilter().code,
        name: getValuesFilter().name,
        description: '',
        lineOfBusinessCode: refLineOfBusinessCode.current
      }, { pageSize: 150, pageNumber: 1 });

    setProductFilters(arr);
  };


  const getInputAdornmentBillingDetail = (row: IBillingDetail, cellId: keyof IBillingDetail) => ({

    toolTip: 'Expression',
    icon: MoreHorizIcon,
    onClickIcon: async (event: any, index: number, row: IBillingDetail) => {
      setValue('currentBillingDetailIndex', index);

      const { productId } = row;

      const exists = allProductsWithOptions.some(p => p.id === productId);

      const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
        await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
      //productFilters.find(p => p.id === productId);
      if (!exists)
        setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

      if ((isFalsy(row.productOptions) || row.productOptions.length === 0)) {
        setValue(`billingDetails.${index}.productOptions`, prod.productOptions);
      }

      setBillingDetailOpen(true);
    }
  })

  const cellEditableBillingDetail = (row: IBillingDetail, cellId: keyof IBillingDetail) => {

    // if(row.type === 'article' && cellId === 'quantity') return true;
    // if(row.type === 'article' && cellId === 'articleOption') return true;

    return ['article', 'packaging', 'consumption'].includes(row.type);
  }

  const [headBillingDetailCells, setHeadBillingDetailCells] = useState<HeadCell<IBillingDetail>[]>([]);
  useEffect(() => {
    setHeadBillingDetailCells([
      //{id:'type', label : t('Type'),  display: false, type: 'string', width: 15},   
      {
        id: 'productName', label: t('Product'), display: true, type: 'string', width: 65,
        getInputAdornment: getInputAdornmentBillingDetail
      },

      // {id:'unityAmount', label : t('Unity price'),  display: true, type: 'numeric', width: 20, 
      //     isEditable: cellEditableBillingDetail, decimalScale: 2},

      //{id:'baseAmount', label : t('Base'),  display: true, type: 'numeric', width: 12, decimalScale: 2, }, 
      {
        id: 'quantity', label: t('Quantity'), display: true, type: 'numeric', width: 15,
        isEditable: cellEditableBillingDetail, decimalScale: 2
      },
      //{id:'loaded', label : t('Loaded'),  display: true, type: 'numeric', width: 12, decimalScale: 2, 
      //  isEditable: cellEditableBillingDetail, isAllowed: cellAllowedLoaded}, 
      //{id:'isDelivered', label : t('Done ?'),  display: true, type: 'boolean', width: 12}, 

      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 20, decimalScale: 2 },
    ]);
  }, [t, i18n]);


  const refAppendBillingDetails = useRef<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateBillingDetail = useRef<(index: number, value: Partial<FieldArray<IBilling>>) => void>(null);
  const refRemoveBillingDetail = useRef<(index: number) => void>(null);


  const billingDetailRowActionIcon = (row: IBillingDetail): ActionIconTableRow<IBilling, IBillingDetail> => {

    const roleEntityBilling = roleEntities.find(re => re.entityName === 'Billing');

    const canDeliverService = !isFalsy(roleEntityBilling) &&
      roleEntityBilling.roleEntityFeatures.some(f => f.featureName === 'DeliverBillingDetailBillingHandler');

    const res: ActionIconTableRow<IBilling, IBillingDetail> = {
      toolTip: 'viewDetails',
      icon: getValues().status === '95' ? RemoveCircleIcon : MoreHorizIcon,
      hasAction: (getValues().status === '95') || (getValues().status === '10' && !row.isDelivered && canDeliverService),  // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IBillingDetail) => {

        if (getValues().status === '95') {
          //(refRemoveBillingDetail.current??emptyFunc)(index);
          removeBillingDetail(index);
          setTotalAmount();
        } else if (getValues().status === '10') {
          // Here we can display action on BillingDetail....
          //setOpenBillingDetailServiceDelivery(true);
        }
      }
    }
    return res;
  }

  const handleBillingDetailSelected = (event: React.MouseEvent<unknown>, index: number, row: IBillingDetail) => {
    setValue('currentBillingDetailIndex', index);
  }

  const handleAddProducts = (event: any) => {

    setOpenProductFilter(true);
  }


  /////// Scrooling of Chip .....
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;

    // 🔹 1. Check if there is any horizontal overflow at all
  const hasOverflow = scrollWidth > clientWidth + EPSILON;

  if (!hasOverflow) {
    setCanScrollLeft(false);
    setCanScrollRight(false);
    return;
  }

    setCanScrollLeft(scrollLeft > EPSILON);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - EPSILON);

    // setCanScrollLeft(el.scrollLeft > 0);
    // setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scrollBy = (offset: number) => {
    scrollRef.current?.scrollBy({
      left: offset,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    // Run once after mount
    requestAnimationFrame(updateScrollState);

    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);


  /////// Scrooling of Chip .....

  const debouncedProductFilter = useRef(
    debounce(async () => {
      //const { customerId, customerFirstName, customerLastName, customerCorporationName, customerPersonType } = getValues();

      const code = getValuesFilter().code;
      const name = getValuesFilter().name;

      const arr = (allProducts.length > 0 && applicationSetup.loadProductOnLogon) ?
        allProducts.filter(p => p.code.toLowerCase().includes(code.toLowerCase()) &&
          p.name.toLowerCase().includes(name.toLowerCase()) &&
          p.lineOfBusinessCode.includes(refLineOfBusinessCode.current))
        : await getBasicProducts(0, getValues().showroomId, getValues().currencyCode, {
          isActive: true,
          code: getValuesFilter().code,
          name: getValuesFilter().name,
          description: '',
          lineOfBusinessCode: refLineOfBusinessCode.current
        }, { pageSize: 150, pageNumber: 1 });

      setProductFilters(arr);

    }, 1000)
  ).current;

  const [showProductOption, setShowProductOption] = useState<boolean>(false);
  const [selectedProductOption, setSelectedProductOption] = useState<IProductOption>(defaultProductOption);

  const handleProductClick = async (productId: number) => {
    //if(billingDetails.some(bd => bd.productId === productId)) return;
    if (getValues().status !== '95') {
      enqueueSnackbar(`${t('You can not add product, because this is billing is already validated')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }

    const exists = allProductsWithOptions.some(p => p.id === productId);
    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    if (isFalsy(prod)) return;

    const { id, description, name, serviceDurationInMinute, isActive, filterOption, lineOfBusinessCode, type,
      defaultPrice, defaultMaxDiscount, defaultMaxLoaded, priceExpression, maxDiscountExpression, maxLoadedExpression,
      productOptions, isTaxable, pictureUrl } = prod;

    const st = store ? store! : defaultStore;

    if (!isActive) {
      enqueueSnackbar(`${t('This product is not active')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    if (['article'].includes(type) && productOptions.length <= 0) {
      enqueueSnackbar(`${t('This product must have at least one option')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    if (!productOptions.some(opt => opt.isAvailable) && st.explicitProductDefinition) {
      enqueueSnackbar(`${t('This product must have at least one option')} : ${t('availability')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    setCurrentProductOptions(productOptions);
    if (productOptions.length > 1) {
      setShowProductOption(true);
    } else {
      await handleProductOptionChoiceClick(productOptions.at(0) ?? { ...defaultProductOption, productId: id });
    }

    setValueFilter('code', '');
    setValueFilter('name', '');
  }

  const [openMessageBox, setOpenMessageBox] = useState<boolean>(false);
  const [messageMessageBox, setMessageMessageBox] = useState<string>('');

  const handleProductOptionChoiceClick = async (productOption: IProductOption) => {

    const { productId } = productOption;

    const exists = allProductsWithOptions.some(p => p.id === productId);
    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    //const prod = productFilters.find(p => p.id === productOption.productId);
    if (isFalsy(prod)) return;


    if (billingDetails.some(bd => bd.productId === prod.id && bd.productOptionId === productOption.id)) {
      setSelectedProductOption(productOption);
      setMessageMessageBox('This option alredy exist for this product, do you want to add another ?');
      setOpenMessageBox(true);

    } else {
      addProductOptionChoice(productOption);
    }

    setShowProductOption(false);
  }

  const addProductOptionChoice = async (productOption: IProductOption) => {

    const { productId } = productOption;

    const exists = allProductsWithOptions.some(p => p.id === productId);
    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    // const prod = productFilters.find(p => p.id === productOption.productId);
    if (isFalsy(prod)) return;

    const { id, description, name, serviceDurationInMinute, isActive, filterOption, lineOfBusinessCode, type,
      defaultPrice, defaultMaxDiscount, defaultMaxLoaded, priceExpression, maxDiscountExpression, maxLoadedExpression,
      productOptions, isTaxable, pictureUrl } = prod;

    const st = store ? store! : defaultStore;

    const articleOptions = refEnumItems2.current?.filter(x => x.enumerationCode === Enum_ARTICLE_OPTION &&
      x.parentEnumerationItemCode === filterOption);
    const articleOption = (articleOptions && articleOptions.length === 1) ? articleOptions[0].code || '' : '';

    const tax = isTaxable ? (getValues().defaultTaxRate * defaultPrice / 100) : 0;

    const countBillingDetails = getValues().billingDetails.length;

    if (['article', 'fee', 'contract', 'maintenance', 'consumption', 'formation'].includes(type) &&
      isFalsy(priceExpression) && isFalsy(maxDiscountExpression) && isFalsy(maxLoadedExpression)) {

      appendBillingDetails({
        ...defaultBillingDetail, type, articleId: id, productId: prod.id,
        productOptions, quantity: 1,
        unityAmount: defaultPrice, baseAmount: defaultPrice, netAmount: defaultPrice, tax, amount: defaultPrice + tax,
        maxDiscountAmount: defaultMaxDiscount, maxLoadedAmount: defaultMaxLoaded,
        productName: name, lineOfBusinessCode,
        productOptionId: productOption.id,
        articleFilterOption: filterOption, articleOption, productPictureUrl: pictureUrl
      } as unknown as IBillingDetail);
    } else {
      setIsWaitingBoxShow(true);
      const res = await evaluateAddedProduct({
        ...getValues(), productId: id, billingDetail: { ...defaultBillingDetail, quantity: 1 },
        currencyCode: watchCurrencyCode, storeId: watchShowroomId
      });
      setIsWaitingBoxShow(false);

      const bilDetail = res.data;

      const bilServiceTasks = (type === 'service' && employeeId > 0) ?
        bilDetail.billingServiceTasks.map(({ taskCode, ...bst }) =>
        ({
          ...bst, employeId: taskCodes.includes(taskCode) ? employeeId : 0,
          employeeFullName: taskCodes.includes(taskCode) ? `${userDescription} - ${userName}` : ''
        })) :
        bilDetail.billingServiceTasks;

      const bilPackagingDetails = (type === 'packaging' && employeeId > 0) ?
        bilDetail.billingPackagingDetails.map(({ ...bpd }) =>
        ({
          ...bpd, billingServiceTasks: bpd.billingServiceTasks.map(({ taskCode, ...bst }) =>
          ({
            ...bst, employeId: taskCodes.includes(taskCode) ? employeeId : 0,
            employeeFullName: taskCodes.includes(taskCode) ? `${userDescription} - ${userName}` : ''
          }))
        })) :
        bilDetail.billingPackagingDetails;

      appendBillingDetails({
        ...bilDetail, type, articleId: id, productId: prod.id,
        productOptions, quantity: 1,
        unityAmount: defaultPrice, baseAmount: defaultPrice, netAmount: defaultPrice, tax, amount: defaultPrice + tax,
        maxDiscountAmount: defaultMaxDiscount, maxLoadedAmount: defaultMaxLoaded,
        productName: name, lineOfBusinessCode,
        productOptionId: productOption.id,
        articleFilterOption: filterOption, articleOption, productPictureUrl: pictureUrl,
        billingServiceTasks: bilServiceTasks, billingPackagingDetails: bilPackagingDetails
      } as unknown as IBillingDetail);
      //const {baseAmount, discount, netAmount, tax, amount, unityAmount, quantity} =   

    }
    setValue('currentBillingDetailIndex', countBillingDetails);
    setTotalAmount();
    setOpenMessageBox(false);

    if ((articleOptions || []).length > 1)
      setBillingDetailOpen(true);
  }


  const [openBillingDiscount, setOpenBillingDiscount] = useState<boolean>(false);
  const handleDiscountOpenClick = (event: any) => {
    setOpenBillingDiscount(true)
  }


  const setTotalAmount = () => {
    const sumNetAmount = sum(getValues().billingDetails.map(({ netAmount }) => netAmount));
    const sumTaxAmount = sum(getValues().billingDetails.map(({ tax }) => tax));
    //const sumAmount = sum( getValues().billingDetails.map( ({amount}) => amount ) );

    const net = sumNetAmount - getValues().discount;
    const tax = sumNetAmount === 0 ? 0 : (net * sumTaxAmount) / sumNetAmount;

    const amount = net + tax;

    setValue('initialNetAmount', sumNetAmount);
    setValue('netAmount', net);
    setValue('tax', tax);
    setValue('amount', amount);
    setValue('unpaidAmount', amount - getValues().amountPaid);
  }

  const debouncedQuantityDiscountLoadedChange = useRef(
    debounce(async () => {

      const index = getValues().currentBillingDetailIndex;

      const billingDetail = getValues().billingDetails.at(index);
      if (isFalsy(billingDetail)) return;

      const { type, quantity, rentalDuration, productOptionId, isTaxable, isDefaultTaxFormula, discount, loaded, amountDiscountedOrLoaded,
        baseAmount, unityAmount, isPriceDependsOnQuantity } = billingDetail!;

      const _discount = discount || 0;
      const _loaded = loaded || 0;
      const _amountDiscountedOrLoaded = amountDiscountedOrLoaded || 0;

      if (isPriceDependsOnQuantity) {
        alert('price depends on quantity'); // TO DO:
      }

      const qtyFactor = ['article', 'packaging', 'consumption'].includes(type) ? quantity : rentalDuration;

      const newNetAmount = ['article', 'packaging', 'consumption', 'rental'].includes(type) ? (unityAmount - _discount + _loaded) * qtyFactor : baseAmount - _discount + _loaded;
      const newBaseAmount = ['article', 'packaging', 'consumption', 'rental'].includes(type) ? unityAmount * qtyFactor : baseAmount;

      const newAmountDiscountedOrLoaded = ['article', 'packaging', 'consumption', 'rental'].includes(type) ? unityAmount - _discount + _loaded : baseAmount - _discount + _loaded;

      if (newNetAmount < 0) {
        enqueueSnackbar(`${t('Invalid data !!!')} : ${newNetAmount}`,
          { variant: 'warning', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000 });
        return;
      }

      let newTax = isTaxable ? (getValues().defaultTaxRate * newNetAmount) / 100 : 0;
      if (!isDefaultTaxFormula) {
        // TO DO: Evaluate base on expression of tax for the product.
      }

      setValue(`billingDetails.${getValues().currentBillingDetailIndex}`,
        {
          ...billingDetail!, baseAmount: newBaseAmount, netAmount: newNetAmount,
          tax: newTax, amount: newNetAmount + newTax, amountDiscountedOrLoaded: newAmountDiscountedOrLoaded
        } as unknown as IBillingDetail,
        { shouldValidate: true, shouldDirty: true });

      setTotalAmount();

    }, 1500)
  ).current;

  const debouncedRentalDurationChange = useRef(
    debounce(async () => {

      const index = getValues().currentBillingDetailIndex;

      const billingDetail = getValues().billingDetails.at(index);
      if (isFalsy(billingDetail)) return;

      const { type, rentalDuration, rentalPeriodicity, productId, isTaxable, isDefaultTaxFormula } = billingDetail!;

      setIsWaitingBoxShow(true);
      const res = await evaluateAddedProduct({
        ...getValues(), productId, billingDetail,
        currencyCode: watchCurrencyCode, storeId: watchShowroomId
      });
      setIsWaitingBoxShow(false);

      const { baseAmount, discount, loaded, tax, unityAmount } = res.data;

      const newNetAmount = (unityAmount - discount + loaded) * rentalDuration;
      let newTax = isTaxable ? (getValues().defaultTaxRate * newNetAmount) / 100 : 0;
      if (!isDefaultTaxFormula) {
        // TO DO: Evaluate base on expression of tax for the product.
      }

      const effectiveDate = billingDetail!.rentalEffectiveDate;
      const expirationDate = addDurationInPeriod(effectiveDate, rentalPeriodicity, rentalDuration);

      setValue(`billingDetails.${getValues().currentBillingDetailIndex}`,
        {
          ...billingDetail!,
          rentalExpirationDate: expirationDate, amountDiscountedOrLoaded: unityAmount - discount + loaded,
          baseAmount, netAmount: newNetAmount, tax: newTax, amount: newNetAmount + newTax
        } as unknown as IBillingDetail);

      setTotalAmount();

    }, 1500)
  ).current;

  const debouncedDefaultTaxRateChange = useRef(
    debounce(async () => {

      const { billingDetails, defaultTaxRate } = getValues();

      setValue('billingDetails',
        billingDetails.map(b => ({
          ...b,
          tax: b.isTaxable ? (b.netAmount * defaultTaxRate) / 100 : 0,
          amount: b.isTaxable ? (b.netAmount * (100 + defaultTaxRate)) / 100 : b.netAmount
        }))
      );

      setTotalAmount();
    }, 1500)
  ).current;


  useEffect(() => {

    setTotalAmount();
  }, [watchDiscount]);


  useEffect(() => {

    debouncedDefaultTaxRateChange();
  }, [watchDefaultTaxRate, debouncedDefaultTaxRateChange]);

  useEffect(() => {

    const subscription = watch(async (value, { name, type }) => {

      //console.log(name);
      //if (name === 'currentBillingDetailIndex') return; // 👈 Add this line

      if (name?.startsWith('billingDetails') && /(quantity|discount|loaded)$/.test(name)) {
        //const match = name!.match(/billingDetails(?:\[(\d+)\]|\.([0-9]+))\.(quantity|discount|loaded)/);
        //console.log({name, match});
        //if(match) setValue('currentBillingDetailIndex', Number(match[1] ?? match[2]), { shouldDirty: false, shouldTouch: false, shouldValidate: false,} );
        debouncedQuantityDiscountLoadedChange();
      } else if (name?.startsWith('billingDetails') && /(rentalDuration|rentalEffectiveDate)$/.test(name)) {
        debouncedRentalDurationChange();
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);


  useEffect(() => {

    debouncedProductFilter();
  }, [watchProductFilterName, watchProductFilterCode]);


  //const [billingDetailIndex, setBillingDetailIndex] = useState<number>(-1);

  const [billingDetailMenuAnchorEl, setBillingDetailMenuAnchorEl] = useState(null);
  const openBillingDetailMenu = Boolean(billingDetailMenuAnchorEl);
  const handleMenuBillingDetailMenuOpen = (event: any, bilDetailIndex: number) => {
    setValue('currentBillingDetailIndex', bilDetailIndex);
    setBillingDetailMenuAnchorEl(event.currentTarget);
  }

  const handleBillingDetailMenuClose = () => {
    setBillingDetailMenuAnchorEl(null);
  }

  const handleRemoveBillingDetail = (event: any) => {
    removeBillingDetail(getValues().currentBillingDetailIndex);
    setTotalAmount();
  }

  const [billingDetailOpen, setBillingDetailOpen] = useState<boolean>(false);
  const handleViewBillingDetail = (event: any) => {
    setBillingDetailOpen(true);
  }


  const tellerOperationRowActionIcon = (tellerOperation: ITellerOperation): ActionIconTableRow<IBilling, ITellerOperation> => {

    const res: ActionIconTableRow<IBilling, ITellerOperation> = {
      toolTip: t('Print'),
      icon: PrintIcon,
      hasAction: (index: number, row: ITellerOperation) => {
        const roleEntityTellerOperation = roleEntities.find(re => re.entityName === 'TellerOperation');
        return roleEntityTellerOperation?.printAllowed ?? false;
      }, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: ITellerOperation) => {
        openEntityPrintDrawer('TellerOperation', row.id);
        //(refRemoveBilling.current??emptyFunc)(index);            
      }
    }
    return res;
  }

  const handleTellerOperationDoubleClick = (event: React.MouseEvent<unknown>, row: ITellerOperation) => {
    const roleEntityTellerOperation = roleEntities.find(re => re.entityName === 'TellerOperation');
    if (isFalsy(roleEntityTellerOperation)) return;

    navigate(`/tellerOperation/${row.id}`);
  }

  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;

  }, [enumItems]);

  const refEnumItems2 = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems2.current = enumItems2;

  }, [enumItems2]);


  useEffect(() => {
    debouncedProductFilter();
  }, []);

  useEffect(() => {
    //setCurrentFormNameAtom( watchIsDirectSale? `${t('Direct sale')}` : `${t('Billing')} - ${t('Sale')}`);
    setCurrentBasicTextFilterProps(basicFilterBilling);
  }, [watchIsDirectSale]);


  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        
    setCurrentFormNameAtom(_id > 0 && watchIsDirectSale ? `${t('Direct sale')} - # ${_id} # -` :
      watchIsDirectSale ? t('Direct sale') :
        _id > 0 && watchIsProforma ? `${t('Proforma')} - # ${_id} # -` :
          watchIsProforma ? `${t('Proforma')}` :
            _id > 0 ? `${t('Billing')} - # ${_id} # -` : t('Billing'));
    if (_id > 0)
      retrieveData('Billing', _id, refetch);
  }, [_id, watchIsDirectSale, watchIsProforma]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      if (!userStores.some(s => s.storeId === _data.showroomId)) {
        enqueueSnackbar(`${t('This billing is not in your scope')} :: ${t('Showroom')}`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
        });
        _newData();
        return;
      }
      reset(_data);
    }
  }, [_data]);


  useEffect(() => {
    async function _() {
      const operations = getValues().id > 0 ? await getTellerOperationsByBilling(getValues().id) : [];  // getMonth of js give the month [0-11]
      setTellerOperations(operations);
    }
    _();
  }, [getValues().id]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _newData();
  }

  const _newData = () => {
    const dt = new Date();
    _setId(0);
    reset({
      ...props, //isDirectSale:watchIsDirectSale,
      showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      currencyCode: applicationSetup.defaultCurrencyCode,
      defaultTaxRate: applicationSetup.defaultTaxRate,
      reference: `${userAlias ?? ''}-${format(dt, 'yyyy/MM/dd-HH')}h${format(dt, 'mm')}`
    });

    resetFilter({ code: '', name: '' });
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {

    if (!checkEntitySaveAuthorization('Sale', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();

    if (data.reference.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (isFalsy(data.currencyCode)) {
      enqueueSnackbar(`${t('Invalid data')} :: ${t('Currency')}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if ((data.amount <= 0 || data.billingDetails.length <= 0)) {
      setIsSaveLoading(false);
      return;
    }

    if ((!applicationSetup.isTaxRateVariable && data.defaultTaxRate !== 0 && data.defaultTaxRate !== applicationSetup.defaultTaxRate)) {
      enqueueSnackbar(`${t('Invalid data')} :: ${t('Tax')} : ${data.defaultTaxRate}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    if (data.showroomId <= 0 || !userStores.some(s => s.storeId === data.showroomId && s.isShowroom)) {
      enqueueSnackbar(`${t('Invalid data')} :: ${t('Showroom')}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    mutate(data);
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('Sale', _id);
  }

  const printData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityPrintDrawer('Sale', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    queryClient.invalidateQueries(['Billing', _id]);
    await retrieveData('Billing', _id, refetch);
    reset(_data);

    const operations = getValues().id > 0 ? await getTellerOperationsByBilling(getValues().id) : [];  // getMonth of js give the month [0-11]
    setTellerOperations(operations);
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={0.5} columnSpacing={1}>


          <Grid item xs={12} sm={6} md={7} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnPrint' onClick={printData} sx={{ display: 'none' }} />
                <Typography variant="h6" id="tableTitle" color="primary" noWrap
                  sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Find product or service'))} ...`}
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', mt: 1 }}>
                {/* LEFT BUTTON */}
                {canScrollLeft && (
                  <IconButton
                    size="small"
                    onClick={() => scrollBy(-SCROLL_OFFSET)}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                )}

                {/* SCROLL CONTAINER */}
                <Box
                  ref={scrollRef}
                  sx={{
                    px: 4, // space for buttons
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {/* ALL */}
                  <MotionChip
                    label={t('ALL')}
                    size="small"
                    clickable
                    onClick={() => handleLineOfBusiness('')}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.96 }}
                    sx={{
                      scrollSnapAlign: 'start',
                      px: 1.5,
                      borderRadius: '999px',
                      fontWeight: 600,
                      bgcolor: lineOfBusinessCode === '' ? 'primary.main' : 'background.paper',
                      color: lineOfBusinessCode === '' ? 'primary.contrastText' : 'text.primary',
                      border: '1px solid',
                      borderColor: lineOfBusinessCode === '' ? 'primary.main' : 'divider',
                    }}
                  />

                  {enumItems
                    ?.filter(e => e.enumerationCode === Enum_LINE_OF_BUSINESS)
                    .map(x => {
                      const selected = lineOfBusinessCode === x.code;

                      return (
                        <MotionChip
                          key={x.code}
                          label={x.name}
                          size="small"
                          clickable
                          onClick={() => handleLineOfBusiness(x.code)}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.96 }}
                          sx={{
                            scrollSnapAlign: 'start',
                            px: 1.5,
                            borderRadius: '999px',
                            fontWeight: 600,
                            bgcolor: selected ? 'primary.main' : 'background.paper',
                            color: selected ? 'primary.contrastText' : 'text.primary',
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                          }}
                        />
                      );
                    })}
                </Box>

                {/* RIGHT BUTTON */}
                {canScrollRight && (
                  <IconButton
                    size="small"
                    onClick={() => scrollBy(SCROLL_OFFSET)}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ mt: 1, width: '100%', display: 'flex', gap: 2 }} >
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="name" variant="outlined" size="small"
                  {...registerFilter('name')} placeholder={t('Filter by name or description')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    readOnly: false,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={(event) => setValueFilter('name', '')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />
                <TextField sx={{ width: 'calc(50% - 8px)' }} id="code" variant="outlined" size="small"
                  {...registerFilter('code')} placeholder={t('Filter by code or barcode')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  InputProps={{
                    readOnly: false,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={(event) => setValueFilter('code', '')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }} />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Grid container rowSpacing={2} columnSpacing={1}>
                  {productFilters.filter(p => !p.isJustForStock).map((prod, idx) => {
                    return (
                      <Grid key={`${prod.id} - ${idx}`} item xs={6} sm={4} md={3} lg={2} component={Paper} 
                          sx={{ borderRadius: 2, ml: 0, }} >
                        <Stack flexDirection='column' textAlign="center" onClick={() => handleProductClick(prod.id)}
                          sx={{
                            padding: 0.25, marging: 5, //borderTopLeftRadius: '15%', borderTopRightRadius: '15%',  
                            borderRadius: '5%',
                            //background: '#ffffff',
                            backgroundImage: 'none',
                            color: 'black',
                            transition: 'background-image 0.3s ease',
                            height: `200px`,
                            width: '100%',
                            cursor: 'pointer',
                            border: `${billingDetails.some(b => b.productId === prod.id) ? 2 : 0}px solid #1AAF9D`
                          }}>
                          <Box sx={{ mt: 1, width: '100%' }} >
                            <div style={{
                              outline: 'none',
                              border: 'none',
                              width: '100%',
                              height: `100px`,
                              borderRadius: '10%',
                              overflow: 'hidden',
                              margin: '0 auto'
                            }}>
                              <img src={`${globalConfig.get().apiUrl}/download/${prod.pictureUrl}`} alt="..." style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '5%' }} />
                            </div>
                          </Box>

                          <Box sx={{ mt: 1, width: '100%' }} >
                            <Typography key={`amount ${prod.defaultPrice} `} sx={{ fontWeight: 'bold', fontSize: 14, textAlign: 'center', width: '100%' }}>
                              {prod.name}
                            </Typography>
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} >
                            <Typography key={`amount ${prod.defaultPrice} `} sx={{ fontWeight: 'bold', fontSize: 14, textAlign: 'center', width: '100%' }}>
                              {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(prod.defaultPrice)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
              {showProductOption && <FormDialog open={showProductOption} maxWidth='xs' height='50vh'
                okText='' cancelText='' title='' onCancel={() => { }}
                onClose={() => { setShowProductOption(false); }} onOk={() => { setShowProductOption(false); }}  >
                <BasicButtonList<IProductOption>
                  items={currentProductOptions}
                  fallbackIcon={BoltIcon}
                  onItemClick={handleProductOptionChoiceClick}
                  valueKey={'id'} displayKey={'name'} title={t('Product options')} />
              </FormDialog>}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={5} component={Paper} sx={{ borderRadius: 2, ml: 0, display: 'flex', flexDirection: 'column', height: '100%' }} >
            <Stack flexDirection='column' sx={{ flex: 1, height: '100%' }}>
              <Box sx={{ mt: 1, width: '100%' }} key={` ${getValues().customer.fullNameFR} ${watchCustomerId}`} >
                <Controller
                  key={`reference - :::`} render={({ field }) => <TextField sx={{ width: `calc(35% - 8px)` }} {...field}
                    label={t('Reference')} inputProps={{ readOnly: false }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton color="primary" onClick={handleClickOpenDefaultReference}>
                            <MoreVertIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }} />}
                  name={`reference`}
                  control={control}
                />

                <Controller name={watchIsProforma ? 'proformaStatus' : 'status'} control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(25% - 8px)' }} id="status"
                      label={t('Status')} inputProps={{ readOnly: true }} >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_OPERATION_STATUS).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller control={control}
                  name='issueDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Issue date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(15% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                    />)}
                />
                <Button variant="outlined" component="button" onClick={handleLinkClickBillingInfo}
                  sx={{ ml: 1, width: '15%' }}
                >{t('More')}</Button>
                {openBillingInfo && <FormDialog open={openBillingInfo} maxWidth='md'
                  okText={t('Close')} cancelText='' title={t('Informations on billing')} onCancel={() => { }}
                  onClose={() => { setOpenBillingInfo(false); }} onOk={() => { setOpenBillingInfo(false); }} >
                  <Stack flexDirection='column'  >
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller name='showroomId' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(32% - 8px)' }} id="showroomId"
                            label={t('Showroom')} inputProps={{ readOnly: getValues().billingDetails.length > 0 }} focused >
                            {userStores && userStores.filter(s => s.isShowroom).map(
                              (x, idx) => <MenuItem key={x.storeId} value={x.storeId}>{x.storeName}</MenuItem>)
                            }
                          </TextField>
                        )} />
                      <Controller name='currencyCode' control={control}
                        render={({ field: { onChange, value } }) => (
                          <TextField select onChange={onChange} value={value} sx={{ width: 'calc(28% - 8px)' }} id="currencyCode"
                            label={t('Currency')} inputProps={{ readOnly: getValues().billingDetails.length > 0 }} >
                            {enumItems && enumItems.filter(e =>
                              e.enumerationCode === Enum_CURRENCY).map(
                                (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                            }
                          </TextField>
                        )}
                      />

                      <Controller
                        render={({ field: { onChange, onBlur, name, value, ref } }) => {
                          return (
                            <NumberFormat
                              label={`${t('Default')} - ${t('Tax')}`} sx={{ width: 'calc(10% - 8px)' }} disabled={false}
                              allowEmptyFormatting={false}
                              control={control}
                              thousandSeparator={lg.includes('fr') ? ' ' : ','}
                              decimalScale={2}
                              onValueChange={(v) => onChange(v.floatValue)}
                              defaultValue={value}
                              value={value}
                              customInput={TextFieldRight}
                            />
                          );
                        }}
                        name={`defaultTaxRate`}
                        control={control}
                      />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <TextField sx={{ width: 'calc(40% - 8px)' }} id="ln" label={t('Customer')}
                        key={` key:: ${watchCustomerId} ${getValues().customer.fullNameFR}`}
                        {...register(lg.startsWith('fr') ? 'customer.fullNameFR' : 'customer.fullNameEN')}
                        inputProps={{ readOnly: true }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              {(isFalsy(watchCustomerId) || watchCustomerId <= 0) ?
                                <IconButton color="primary" onClick={handleClickOpenCustomer}>
                                  <ArrowDropDownCircleIcon />
                                </IconButton> :
                                <IconButton color="primary" onClick={handleClickRemoveCustomer}>
                                  <RemoveCircleIcon />
                                </IconButton>
                              }
                            </InputAdornment>
                          )
                        }} />
                    </Box>
                    <BasicBillingInfoForm control={control} setValue={setValue} getValues={getValues} watch={watch} />
                  </Stack>

                </FormDialog>}
                {showDefaultReference && <FormDialog open={showDefaultReference} maxWidth='xs' height='50vh'
                  okText={t('Close')} cancelText='' title='' onCancel={() => { }}
                  onClose={() => { setShowDefaultReference(false); }} onOk={() => { setShowDefaultReference(false); }}  >
                  <BasicButtonList<IEnumerationItem>
                    items={(enumItems || []).filter(e => e.enumerationCode === Enum_DEFAULT_BILLING_REFERENCE)}
                    fallbackIcon={BoltIcon}
                    onItemClick={handleDefaultReferenceChoiceClick}
                    valueKey={'code'} displayKey={'name'} title={t('Default billing reference')} />
                </FormDialog>}
              </Box>
              {(groupBillingBillings && groupBillingBillings.length > 0) &&
                <Box sx={{ mt: 0.25, width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Link href="#" onClick={(event) => navigate(`/groupBilling/${groupBillingBillings[0].groupBillingId}`)}
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                    {`${t('This billing belong to a group billing')} : ${groupBillingBillings[0].groupBillingReference} `}
                  </Link>
                </Box>}
              {(getValues().billingDetails.length > 0 && getValues().amount > 0) &&
                <Box sx={{ mt: 0.25, width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'left' }}>
                  <Link href="#amount-box"
                    sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                    {`${t('The total amount of this billing is')} : ${new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().amount)}`}
                  </Link>
                </Box>}
              <Box key={` ${getValues().billingDetails.map(b => `x:${b.amount} y:${b.quantity} z:${b.unityAmount}`).join('-')}`}
                sx={{ mt: 0.25, width: '100%', cursor: 'pointer', display: 'flex' }}  >
                <ArrayFieldTableEx<IBilling, IBillingDetail, 'id'>
                  key={`::Details:: ${getValues().billingDetails.map(b => `x:${b.amount} y:${b.quantity} z:${b.unityAmount}`).join('-')}`}
                  mainObject={getValues()} fieldKey='id'
                  headCells={headBillingDetailCells} rowsPathName='billingDetails'
                  title={t('Billings')} rowActionIcon={billingDetailRowActionIcon}
                  onRowSelected={handleBillingDetailSelected}
                  //onRowDoubleClick={handleBillingDetailRowDoubleClick}

                  refAppend={refAppendBillingDetails as MutableRefObject<(value: Partial<FieldArray<IBilling>> | Partial<FieldArray<IBilling>>[], options?: FieldArrayMethodProps) => void>}
                  refUpdate={refUpdateBillingDetail as MutableRefObject<(index: number, value: Partial<FieldArray<IBilling>>) => void>}
                  refRemove={refRemoveBillingDetail as MutableRefObject<(index: number) => void>}

                  //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                  //displayMore={undefined}
                  toolbarActions={[

                    { toolTip: `${t('Add')}...`, onClickIcon: handleAddProducts, icon: AddCircleIcon, },

                  ]}
                  canCheckRow={false} //canFilterColumn={false} canDisplayColumnHeader={false}
                />
                {openProductFilter && <FormDialog open={openProductFilter} maxWidth='md'
                  okText='' cancelText='' title={t('Product')} onCancel={() => { }}
                  onClose={() => { setOpenProductFilter(false); }} onOk={() => { setOpenProductFilter(false); }}  >
                  <BasicTextFilterForm<IProduct> {...basicFilterProduct} />
                </FormDialog>}
                {billingDetailOpen && <FormDialog open={billingDetailOpen} maxWidth='sm' height='60vh'
                  okText={t('Close')} cancelText='' title='' onCancel={() => { }}
                  onClose={() => { setBillingDetailOpen(false); }} onOk={() => { setBillingDetailOpen(false); }}  >
                  <BasicBillingDetailForm control={control} getValues={getValues} setValue={setValue} watch={watch} />
                </FormDialog>}
                {openMessageBox && <MessageBoxDialog open={openMessageBox} maxWidth='sm' fullWidth
                  message={messageMessageBox}
                  okText={t('Yes')} cancelText={t('No')} title={`Univsoft ...`} onCancel={() => { setOpenMessageBox(false); }}

                  onClose={() => { setOpenMessageBox(false); }} onOk={(evt) => addProductOptionChoice(selectedProductOption)} />
                }
                {openCustomerDetails && <FormDialog open={openCustomerDetails} maxWidth='md' height='45vh'
                  okText={t('Close')} cancelText='' title='' onCancel={() => { }}
                  onClose={() => { setOpenCustomerDetails(false); }} onOk={() => { setOpenCustomerDetails(false); }}  >
                  <BasicPersonForm personKeyPath='customerId' personPath='customer' />
                </FormDialog>
                }
              </Box>
              <Box id="amount-box" sx={{ border: '1px solid #ddd', padding: 2, textAlign: 'right', /*bgcolor: 'white'*/ }}>
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }} >
                    <Typography key={`paid amount ${getValues().amountPaid} `} sx={{ fontWeight: 'bold', textAlign: 'left', width: '100%' }}>
                      {`${t('Amount')} (s)`}
                    </Typography>
                  </Box>
                  <Stack flexDirection='row'>
                    {/* Initial */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                        {t('Initial')}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().initialNetAmount)}
                      </Typography>
                    </Box>
                    {/* Discount */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography
                        onClick={applicationSetup.isBillingDiscountEnabled ? handleDiscountOpenClick : undefined}
                        sx={{
                          textAlign: 'left', fontWeight: 'bold',
                          color: applicationSetup.isBillingDiscountEnabled ? 'primary.main' : 'text.disabled',
                          cursor: applicationSetup.isBillingDiscountEnabled ? 'pointer' : 'default',
                          textDecoration: applicationSetup.isBillingDiscountEnabled ? 'underline' : 'none',
                        }}
                      >
                        {t('Discount')}
                      </Typography>
                      <Typography
                        onClick={applicationSetup.isBillingDiscountEnabled ? handleDiscountOpenClick : undefined}
                        sx={{
                          textAlign: 'right', fontWeight: 'bold',
                          color: applicationSetup.isBillingDiscountEnabled ? 'primary.main' : 'text.disabled',
                          cursor: applicationSetup.isBillingDiscountEnabled ? 'pointer' : 'default'
                        }}
                      >
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().discount)}
                      </Typography>
                    </Box>
                    {/* Net */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                        {t('Net')}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().netAmount)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack flexDirection='row'>
                    {/* Total */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                        {t('Total')}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().amount)}
                      </Typography>
                    </Box>
                    {/* Paid */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                        {t('Paid')}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().amountPaid)}
                      </Typography>
                    </Box>
                    {/* Unpaid */}
                    <Box sx={{ width: '33.33%', display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                        {t('Unpaid')}
                      </Typography>
                      <Typography sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(getValues().unpaidAmount)}
                      </Typography>
                    </Box>
                    {openBillingDiscount && <FormDialog open={openBillingDiscount} maxWidth='sm' height='30vh'
                      okText={t('Close')} cancelText='' title={t('Discount')} onCancel={() => { }}
                      onClose={() => { setOpenBillingDiscount(false); }} onOk={() => { setOpenBillingDiscount(false); }}  >
                      <Stack flexDirection='column'>
                        <Box sx={{ mt: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }} >
                          <Controller
                            render={({ field: { onChange, onBlur, name, value, ref } }) => {
                              return (
                                <NumberFormat
                                  label={`${t('Rate')} ('%')`} sx={{ width: 'calc(50% - 8px)' }} disabled={false}
                                  allowEmptyFormatting={false}
                                  control={control}
                                  thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                  decimalScale={2}
                                  isAllowed={(values: NumberFormatValues) => {
                                    return (values.floatValue || 0) >= 0 && (values.floatValue || 0) <= applicationSetup.maxBillingDiscountRate;
                                  }}
                                  onValueChange={(v) => {
                                    const rate = v.floatValue || 0;
                                    onChange(rate);
                                    // Set corresponding amount
                                    const amount = (rate / 100) * getValues().initialNetAmount;
                                    setValue('discount', parseFloat(amount.toFixed(2)));
                                  }}
                                  //onValueChange={ (v) => onChange(v.floatValue) }                                                    
                                  defaultValue={value}
                                  value={value}
                                  customInput={TextFieldRight}
                                />
                              );
                            }}
                            name={`discountRate`}
                            control={control}
                          />
                          <Controller
                            render={({ field: { onChange, onBlur, name, value, ref } }) => {
                              return (
                                <NumberFormat
                                  label={`${t('Amount')} `} sx={{ width: 'calc(50% - 8px)' }} disabled={false}
                                  allowEmptyFormatting={false}
                                  control={control}
                                  thousandSeparator={lg.includes('fr') ? ' ' : ','}
                                  decimalScale={2}
                                  isAllowed={(values: NumberFormatValues) => {
                                    return (values.floatValue || 0) >= 0 && (values.floatValue || 0) <= getValues().initialNetAmount * (applicationSetup.maxBillingDiscountRate / 100);
                                  }}

                                  onValueChange={(v) => {
                                    const amount = v.floatValue || 0;
                                    onChange(amount);
                                    // Set corresponding rate
                                    const { initialNetAmount } = getValues();
                                    const rate = (initialNetAmount === 0) ? 0 : (amount / initialNetAmount) * 100;
                                    setValue('discountRate', parseFloat(rate.toFixed(2)));
                                  }}
                                  defaultValue={value}
                                  value={value}
                                  customInput={TextFieldRight}
                                />
                              );
                            }}
                            name={`discount`}
                            control={control}
                          />
                        </Box>
                      </Stack>
                    </FormDialog>
                    }
                  </Stack>
                  <Box sx={{ mt: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                    {(getValues().status === '95' && getValues().amount > 0 && checkEntitySaveAuthorization('Sale', getValues().id)) &&
                      <Button variant={getValues().id > 0 ? 'outlined' : 'contained'}
                        onClick={saveData}
                        sx={{
                          //background: mainInformation.couleurBoutonPanier, 
                          //backgroundImage: 'linear-gradient(to right, #F26666, #400C07)',
                          //color: mainInformation.couleurTexteBoutonPanier, 
                          marginTop: '5px', fontFamily: 'Poppins !important', width: `${'45'}%`, height: '45px',
                          borderRadius: '15px'
                        }} >
                        {`${t('Save')} (${getValues().id > 0 ? t('Modify') : t('Create')})`}
                      </Button>}
                    {(getValues().id > 0) && <Button variant={getValues().status === '10' ? 'outlined' : 'contained'}
                      onClick={actionData}
                      sx={{
                        //background: mainInformation.couleurBoutonPanier, 
                        //backgroundImage: 'linear-gradient(to right, #F26666, #400C07)',
                        //color: mainInformation.couleurTexteBoutonPanier, 
                        marginLeft: '5px',
                        marginTop: '5px', fontFamily: 'Poppins !important', width: `${'45'}%`, height: '45px',
                        borderRadius: '15px'
                      }} >
                      {t('Do an action')}
                    </Button>}
                    {(getValues().status === '10' && getValues().unpaidAmount > 0) &&
                      <Button variant={'contained'}
                        onClick={actionData}
                        sx={{
                          //background: mainInformation.couleurBoutonPanier, 
                          //backgroundImage: 'linear-gradient(to right, #F26666, #400C07)',
                          //color: mainInformation.couleurTexteBoutonPanier, 
                          marginLeft: '5px',
                          marginTop: '5px', fontFamily: 'Poppins !important', width: `${'45'}%`, height: '45px',
                          borderRadius: '15px'
                        }} >
                        {t('Pay the billing')}
                      </Button>}
                  </Box>
                </Stack>
              </Box>
              {((getValues().id || 0) > 0 && (tellerOperations || []).length > 0 && roleEntities.some(re => re.entityName === 'TellerOperation')) &&
                <Box sx={{ mt: 3, width: '100%' }} >
                  <EnhancedTable<ITellerOperation>
                    rows={tellerOperations || []}
                    headCells={[
                      //{id:'id', label : t('Id'),  display: true, type: 'numeric', },
                      { id: 'reference', label: t('Reference'), display: true, type: 'string', width: 50 },

                      { id: 'amount', label: t('Amount'), display: true, type: 'numeric', width: 25 },
                      { id: 'operationDate', label: t('Operation date'), display: true, type: 'date', width: 25 },
                      //{id:'issueDate', label : t('Issue date'),  display: true, type: 'date'},
                      //{id:'isCancelled', label : t('Cancelled ?'),  display: true, type: 'boolean',  width: 20},
                    ]}
                    title={t(`List of payments`)} objKey='operationDate'
                    stateSelected={undefined}
                    onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='operationDate'

                    onRowDoubleClick={handleTellerOperationDoubleClick}
                    rowActionIcon={tellerOperationRowActionIcon}
                    toolbarActions={[
                      // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                    ]}
                  />
                </Box>}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}

